import type {
  ScenarioDto,
  ScenarioGuardrailPrecheckResultDto,
  ScenarioTypedTaskPlanDto,
} from '@terra/shared-contracts-internal-network';
import { resolveWeaknessCatalogEntry } from '@terra/shared-contracts-internal-network';
import type { ScenarioStatusSchema } from '@terra/shared-schemas-orchestrator';
import type { z } from 'zod';

export type ScenarioStatus = z.infer<typeof ScenarioStatusSchema>;

export type ScenarioStepVisualState =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'blocked'
  | 'skipped';

export const SCENARIO_BOOKEND_STEP_ACTIONS = new Set([
  'qualify',
  'collect-evidence',
]);

export const isScenarioTechniqueStepAction = (action: string): boolean =>
  !SCENARIO_BOOKEND_STEP_ACTIONS.has(action);

export type ScenarioTypedTaskPlanStep =
  ScenarioTypedTaskPlanDto['steps'][number];

export const resolveFirstTechniqueStepIndex = (
  steps: Pick<ScenarioTypedTaskPlanStep, 'action'>[],
): number => {
  const index = steps.findIndex((step) =>
    isScenarioTechniqueStepAction(step.action),
  );
  return index === -1 ? steps.length : index;
};

export const resolveScenarioStepVisualState = ({
  stepIndex,
  stepAction,
  scenarioStatus,
  firstTechniqueStepIndex,
}: {
  stepIndex: number;
  stepAction: string;
  scenarioStatus: ScenarioStatus;
  firstTechniqueStepIndex: number;
}): ScenarioStepVisualState => {
  const isQualify = stepAction === 'qualify';
  const isEvidence = stepAction === 'collect-evidence';
  const isTechnique = isScenarioTechniqueStepAction(stepAction);

  switch (scenarioStatus) {
    case 'DRAFT':
    case 'PRECHECK_PASSED':
      return 'pending';
    case 'PRECHECK_BLOCKED':
      if (isQualify) {
        return 'completed';
      }
      if (isTechnique) {
        return 'blocked';
      }
      if (isEvidence) {
        return 'pending';
      }
      return 'blocked';
    case 'RUNNING':
      if (stepIndex < firstTechniqueStepIndex) {
        return 'completed';
      }
      if (stepIndex === firstTechniqueStepIndex) {
        return 'running';
      }
      return 'pending';
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
    case 'STOPPED':
      if (isQualify) {
        return 'completed';
      }
      if (isTechnique) {
        return 'failed';
      }
      return 'skipped';
    default:
      return 'pending';
  }
};

export const resolveScenarioStepDescription = ({
  step,
  visualState,
  guardrailPrecheckResult,
}: {
  step: ScenarioTypedTaskPlanStep;
  visualState: ScenarioStepVisualState;
  guardrailPrecheckResult: ScenarioGuardrailPrecheckResultDto | null;
}): string => {
  if (
    visualState === 'blocked' &&
    guardrailPrecheckResult?.blockedReason &&
    guardrailPrecheckResult.blockedReason.trim().length > 0
  ) {
    return guardrailPrecheckResult.blockedReason;
  }

  return step.description;
};

export type ScenarioDiscoverySection = 'preflight' | 'collection' | 'evidence';

export const resolveDiscoveryStepSection = (
  action: string,
): ScenarioDiscoverySection => {
  if (action === 'qualify') {
    return 'preflight';
  }

  if (action === 'collect-evidence') {
    return 'evidence';
  }

  return 'collection';
};

export type ScenarioStepTimelineItem = {
  id: string;
  action: string;
  title: string;
  description: string;
  technique?: string;
  command?: string;
  prerequisites?: string;
  expectedOutput?: string;
  visualState: ScenarioStepVisualState;
  discoverySection?: ScenarioDiscoverySection;
  weaknessId?: string;
  weaknessName?: string;
  mitreTechniqueId?: string;
  weaknessExplanation?: string;
};

const resolveStepWeakness = ({
  step,
  signalCategory,
}: {
  step: ScenarioTypedTaskPlanStep;
  signalCategory?: string;
}) => {
  if (!isScenarioTechniqueStepAction(step.action)) {
    return null;
  }

  const hasStepSpecificKey = Boolean(step.edgeType || step.command);
  return resolveWeaknessCatalogEntry({
    category: hasStepSpecificKey ? undefined : signalCategory,
    command: step.command ?? step.technique,
    edgeType: step.edgeType,
    technique: step.technique,
  });
};

export const buildScenarioStepTimelineItems = ({
  steps,
  scenarioStatus,
  guardrailPrecheckResult,
  formatAction,
  scenarioId,
  includeDiscoverySections = false,
  signalCategory,
}: {
  steps: ScenarioTypedTaskPlanStep[];
  scenarioStatus: ScenarioStatus;
  guardrailPrecheckResult: ScenarioGuardrailPrecheckResultDto | null;
  formatAction: (action: string) => string;
  scenarioId: string;
  includeDiscoverySections?: boolean;
  signalCategory?: string;
}): ScenarioStepTimelineItem[] => {
  const firstTechniqueStepIndex = resolveFirstTechniqueStepIndex(steps);

  return steps.map((step, stepIndex) => {
    const visualState = resolveScenarioStepVisualState({
      stepIndex,
      stepAction: step.action,
      scenarioStatus,
      firstTechniqueStepIndex,
    });

    const weakness = resolveStepWeakness({ step, signalCategory });

    return {
      id: `${scenarioId}-${step.action}-${stepIndex}`,
      action: step.action,
      title: step.technique ?? formatAction(step.action),
      description: resolveScenarioStepDescription({
        step,
        visualState,
        guardrailPrecheckResult,
      }),
      technique: step.technique,
      command: step.command,
      prerequisites: step.prerequisites,
      expectedOutput: step.expectedOutput,
      visualState,
      discoverySection: includeDiscoverySections
        ? resolveDiscoveryStepSection(step.action)
        : undefined,
      weaknessId: weakness?.weaknessId,
      weaknessName: weakness?.shortName,
      mitreTechniqueId: weakness?.mitreTechniqueId ?? undefined,
      weaknessExplanation: weakness?.explanation,
    };
  });
};

export const buildScenarioStepTimelineItemsFromScenario = ({
  scenario,
  formatAction,
}: {
  scenario: Pick<
    ScenarioDto,
    | 'id'
    | 'status'
    | 'guardrailPrecheckResult'
    | 'typedTaskPlan'
    | 'actionClass'
  >;
  formatAction: (action: string) => string;
}): ScenarioStepTimelineItem[] => {
  const steps = scenario.typedTaskPlan?.steps ?? [];
  if (steps.length === 0) {
    return [];
  }

  return buildScenarioStepTimelineItems({
    steps,
    scenarioStatus: scenario.status,
    guardrailPrecheckResult: scenario.guardrailPrecheckResult,
    formatAction,
    scenarioId: scenario.id,
    includeDiscoverySections: scenario.actionClass === 'DISCOVERY',
    signalCategory: scenario.typedTaskPlan?.signalCategory,
  });
};
